/**
 * Milestone status — single source of truth (API Title Case values).
 */

export const MILESTONE_STATUS = {
  PLANNED: "Planned",
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  DELAYED: "Delayed",
  CANCELLED: "Cancelled",
  /** Dashboard / sponsor-widget aggregate (not typically set in create form). */
  UPCOMING: "Upcoming",
} as const;

export const MILESTONE_STATUS_VALUES = Object.values(MILESTONE_STATUS);

export type MilestoneStatus =
  (typeof MILESTONE_STATUS)[keyof typeof MILESTONE_STATUS];

/** Dropdown options for create/edit (excludes dashboard-only Upcoming). */
export const MILESTONE_STATUSES: { value: MilestoneStatus; label: string }[] =
  MILESTONE_STATUS_VALUES.filter(
    (value) => value !== MILESTONE_STATUS.UPCOMING,
  ).map((value) => ({ value, label: value }));

export const MILESTONE_STATUS_COLORS: Record<
  MilestoneStatus,
  { bg: string; text: string }
> = {
  [MILESTONE_STATUS.COMPLETED]: { bg: "#22C55E20", text: "#22C55E" },
  [MILESTONE_STATUS.IN_PROGRESS]: { bg: "#3B82F620", text: "#3B82F6" },
  [MILESTONE_STATUS.DELAYED]: { bg: "#EF444420", text: "#EF4444" },
  [MILESTONE_STATUS.PLANNED]: { bg: "#94A3B820", text: "#94A3B8" },
  [MILESTONE_STATUS.CANCELLED]: { bg: "#64748B20", text: "#64748B" },
  [MILESTONE_STATUS.UPCOMING]: { bg: "#EFF6FF", text: "#0B5FFF" },
};

const canonicalize = (value: string): string =>
  value.toLowerCase().replace(/[\s/\-_]+/g, "");

const STATUS_BY_KEY: Record<string, MilestoneStatus> = Object.values(
  MILESTONE_STATUS,
).reduce(
  (acc, value) => {
    acc[canonicalize(value)] = value;
    return acc;
  },
  {} as Record<string, MilestoneStatus>,
);

export const tryNormalizeMilestoneStatus = (
  status: string,
): MilestoneStatus | null => {
  if (!status?.trim()) {
    return null;
  }
  if (MILESTONE_STATUS_VALUES.includes(status as MilestoneStatus)) {
    return status as MilestoneStatus;
  }
  return STATUS_BY_KEY[canonicalize(status)] ?? null;
};

export const normalizeMilestoneStatus = (status: string): MilestoneStatus =>
  tryNormalizeMilestoneStatus(status) ?? MILESTONE_STATUS.PLANNED;

/** Strict compare: unknown status never matches (no Planned default). */
export const isMilestoneStatus = (
  status: string,
  target: MilestoneStatus,
): boolean => tryNormalizeMilestoneStatus(status) === target;

export const isAnyMilestoneStatus = (
  status: string,
  ...targets: MilestoneStatus[]
): boolean => {
  const normalized = tryNormalizeMilestoneStatus(status);
  return normalized !== null && targets.includes(normalized);
};
